import React, { useState } from 'react';
import { TrendingUp, Users, Target, DollarSign } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

const ROICalculator = () => {
  const [inputs, setInputs] = useState({
    monthlyLeads: 1000,
    dealSize: 10000,
  });

  // Calculate metrics
  const monthlyMeetings = Math.round(inputs.monthlyLeads * 0.03); // 3% conversion to meetings
  const monthlyRevenue = monthlyMeetings * inputs.dealSize * 0.25; // 25% close rate
  const annualRevenue = monthlyRevenue * 12;
  const roi = ((annualRevenue - (inputs.monthlyLeads * 12 * 0.5)) / (inputs.monthlyLeads * 12 * 0.5) * 100).toFixed(0);

  return (
    <section className="relative py-20 overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:14px_24px] opacity-20" />

      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-white mb-4">Calculate Your ROI</h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            See how ACOS can transform your outreach into meetings and revenue
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-8"
          >
            {/* Left Column - Inputs */}
            <div className="space-y-8 bg-black/30 backdrop-blur-sm p-8 rounded-2xl border border-white/10">
              <div>
                <h3 className="text-xl font-semibold text-white flex items-center gap-2 mb-6">
                  <Users className="w-5 h-5 text-purple-400" />
                  Your Numbers
                </h3>

                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-gray-400">Monthly Leads Contacted</label>
                      <span className="text-white font-medium">{inputs.monthlyLeads.toLocaleString()}</span>
                    </div>
                    <input 
                      type="range"
                      min="100"
                      max="5000"
                      step="100"
                      value={inputs.monthlyLeads}
                      onChange={(e) => setInputs(prev => ({ ...prev, monthlyLeads: parseInt(e.target.value) }))}
                      className="w-full h-2 bg-purple-900/50 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-purple-500 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer hover:[&::-webkit-slider-thumb]:bg-purple-400 transition-colors"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-gray-400">Average Deal Size</label>
                      <span className="text-white font-medium">${inputs.dealSize.toLocaleString()}</span>
                    </div>
                    <input 
                      type="range"
                      min="1000"
                      max="100000"
                      step="1000"
                      value={inputs.dealSize}
                      onChange={(e) => setInputs(prev => ({ ...prev, dealSize: parseInt(e.target.value) }))}
                      className="w-full h-2 bg-purple-900/50 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-purple-500 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer hover:[&::-webkit-slider-thumb]:bg-purple-400 transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Results */}
            <div className="bg-gradient-to-br from-purple-500/10 to-fuchsia-500/10 backdrop-blur-sm p-8 rounded-2xl border border-purple-500/20">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                  Expected Results
                </h3>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="text-sm text-gray-400 mb-1">Monthly Qualified Meetings</div>
                  <div className="text-3xl font-bold text-white">
                    ~{monthlyMeetings} <span className="text-lg text-gray-400">meetings</span>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-400 mb-1">Monthly Revenue</div>
                  <div className="text-3xl font-bold text-white">
                    ${monthlyRevenue.toLocaleString()} <span className="text-lg text-gray-400">/ month</span>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-400 mb-1">Annual Revenue</div>
                  <div className="text-3xl font-bold text-white">
                    ${annualRevenue.toLocaleString()} <span className="text-lg text-gray-400">/ year</span>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-400 mb-1">ROI</div>
                  <div className="text-3xl font-bold text-green-400">
                    {roi}% <span className="text-lg text-gray-400">return</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-xs text-gray-500">
                * Based on industry average of 3% email-to-meeting and 25% meeting-to-deal conversion rates
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator; 